import { Injectable, inject } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, Route, Router, UrlSegment } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';
import { PermissionsService } from 'src/app/core/menu/permissions.service';
import { MENU_SVG_ICONS } from 'src/app/core/menu/menu-svg-icons';
import { UserPermissions } from 'src/app/models/user-permissions.interface';
import { boSwalIcon } from 'src/app/shared/utils/bo-swal';

/** Empresa ficticia del dashboard de ejemplo («WAIIS S.L.»). */
export const DEMO_CORPORATION_ID = 'ejemplo';
export const DEMO_COMPANY_NAME = 'WAIIS S.L.';

/**
 * Páginas del menú del ejemplo, bajo `/demo/corporation/ejemplo/` (también su
 * `data-demo-nav`). Las de Taxi/VTC llevan dos segmentos (#20261103-104013).
 */
export const DEMO_PAGES = [
  'routes-map',
  'usage',
  'users',
  'travel',
  'sustainability',
  'funnel',
  'incentives',
  'pmst',
  'bonificacion',
  'taxi-vtc/rides',
  'taxi-vtc/bookings',
  'taxi-vtc/review',
];

/**
 * Detalles que también se abren dentro del ejemplo (#20261103-104013): una
 * reserva y su justificante, y una revisión. `:id` es cualquier segmento.
 */
export const DEMO_DETAILS = ['taxi-vtc/bookings/:id', 'taxi-vtc/bookings/:id/receipt', 'taxi-vtc/review/:id'];

/**
 * Rutas que abren algo para guardar: el asistente de «Nueva reserva» acabaría en
 * escrituras que el ejemplo no responde, así que dice «Para guardar» (decisión
 * del cambio #20261103-104013). Se miran antes que los detalles (`new` casaría
 * con `bookings/:id`).
 */
export const DEMO_WRITE_PAGES = ['taxi-vtc/bookings/new'];

/** ¿Casa la ruta (segmentos tras `corporation/ejemplo`) con el patrón? `:id` es cualquier segmento. */
export function matchesDemoPage(pattern: string, segments: string[]): boolean {
  const parts = pattern.split('/');
  return parts.length === segments.length && parts.every((p, i) => p === ':id' || p === segments[i]);
}

/** Paso 1 del registro: a donde lleva «Crear mi espacio». */
export const DEMO_SIGNUP_ROUTE = ['/auth/registration'];

/**
 * Sesión ficticia del dashboard de ejemplo: el administrador de «WAIIS S.L.»,
 * con las páginas que enseña el ejemplo. Solo vive mientras se está en `/demo`.
 */
const DEMO_PERMISSIONS: UserPermissions = {
  id: 'demo',
  name: DEMO_COMPANY_NAME,
  email: '',
  roles: [
    {
      role_name: 'corporation_admin',
      permission_name: 'corporation_admin',
      scope_type: 'corporation',
      scope_id: DEMO_CORPORATION_ID,
    } as UserPermissions['roles'][number],
  ],
  pages: [],
  actions: [],
  partner: null,
  tutored: [],
  trial: null,
};

/**
 * Dashboard de ejemplo sin registro (#20261023-090014): mientras está activo,
 * `DemoInterceptor` responde las lecturas con datos fijos y corta las
 * escrituras con «Para guardar, crea tu espacio». Se entra por la ruta
 * pública `/demo`; al salir, vuelve la sesión que hubiera.
 */
@Injectable({ providedIn: 'root' })
export class DemoModeService {
  active = false;
  private previous: UserPermissions | null = null;
  private blocking = false;

  constructor(
    private permissionsService: PermissionsService,
    private router: Router,
    private translate: TranslateService,
  ) {
    // Red de seguridad: si una navegación acaba (o se corta) fuera de `/demo`
    // con el ejemplo aún activo, se sale; si no, el interceptor dejaría el
    // resto de la app sin back.
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError))
      .subscribe(() => {
        if (this.active && !isDemoUrl(this.router.url)) this.leave();
      });
  }

  enter(): void {
    if (this.active) return;
    this.active = true;
    this.previous = this.permissionsService.currentPermissions;
    this.permissionsService.setCurrentCorporationId(DEMO_CORPORATION_ID);
    this.permissionsService.setPermissions(DEMO_PERMISSIONS);
  }

  leave(): void {
    if (!this.active) return;
    this.active = false;
    this.permissionsService.setCurrentCorporationId(null);
    if (this.previous) {
      this.permissionsService.setPermissions(this.previous);
    } else {
      this.permissionsService.clearPermissions();
    }
    this.previous = null;
  }

  /**
   * CR4: lo que no está en el ejemplo (detalles, incentivos, Ajustes…) no
   * guardaría nada: avisa de que se verá en tu espacio.
   */
  notInDemo(): void {
    this.notice('DEMO.NOT_IN_DEMO');
  }

  /** CR8: lo que guardaría algo se corta con este aviso. */
  blockWrite(): void {
    this.notice('DEMO.SAVE_BLOCKED');
  }

  /** Aviso del ejemplo con «Crear mi espacio» y «Seguir explorando»; uno solo a la vez, sea cual sea. */
  private notice(prefix: string): void {
    if (this.blocking) return;
    this.blocking = true;
    const t = (key: string) => this.translate.instant(key);
    Swal.fire({
      ...boSwalIcon(MENU_SVG_ICONS['sparkles'], 'brand'),
      title: t(`${prefix}.TITLE`),
      text: t(`${prefix}.TEXT`),
      showCloseButton: true,
      showCancelButton: true,
      confirmButtonText: t('DEMO.CREATE_SPACE'),
      cancelButtonText: t('DEMO.KEEP_EXPLORING'),
    }).then((result) => {
      this.blocking = false;
      if (result.isConfirmed) this.router.navigate(DEMO_SIGNUP_ROUTE);
    });
  }
}

function isDemoUrl(url: string): boolean {
  return /^\/demo(\/|\?|#|$)/.test(url);
}

/**
 * Las páginas del ejemplo navegan con rutas absolutas de su empresa
 * (`/corporation/ejemplo/...`), que sacarían de `/demo` al login. Con el ejemplo
 * abierto, esta ruta (en `app-routing`, antes que el resto) las recoge: las
 * páginas del ejemplo y sus detalles (`DEMO_PAGES`, `DEMO_DETAILS`) se quedan
 * dentro de `/demo`, lo que abriría algo para guardar (`DEMO_WRITE_PAGES`) dice
 * «Para guardar» y lo demás (otros detalles, secciones que no están en el
 * ejemplo) se queda donde está con el aviso de que se verá en tu espacio (CR4).
 * Fuera del ejemplo no casa y la navegación sigue como siempre.
 */
export const DEMO_ESCAPE_ROUTE: Route = {
  matcher: (segments: UrlSegment[]) =>
    segments[0]?.path === 'corporation' && segments[1]?.path === DEMO_CORPORATION_ID ? { consumed: segments } : null,
  canMatch: [() => inject(DemoModeService).active],
  canActivate: [
    (_route: unknown, state: { url: string }) => {
      const router = inject(Router);
      const tree = router.parseUrl(state.url);
      const segments = tree.root.children['primary']?.segments.map((s) => s.path) ?? [];
      const page = segments.slice(2);
      if (DEMO_WRITE_PAGES.some((p) => matchesDemoPage(p, page))) {
        inject(DemoModeService).blockWrite();
        return false;
      }
      if ([...DEMO_PAGES, ...DEMO_DETAILS].some((p) => matchesDemoPage(p, page))) {
        return router.createUrlTree(['/demo', ...segments], { queryParams: tree.queryParams });
      }
      inject(DemoModeService).notInDemo();
      return false;
    },
  ],
  children: [],
};
